import useMessageMutation from "@/hooks/useMessageMutation";
import { cn } from "@/libraries/cn";
import { MESSAGE_TAG, type PostCreateMessageBodySchema } from "@/schemas";
import type { GroupMember, MessageDiscussion } from "@/types";
import { useNavigate } from "@tanstack/react-router";
import isEqual from "fast-deep-equal";
import { isEmpty, omit } from "lodash";
import { Suspense, lazy, memo, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import type { MessageStoreState } from "../store";

interface MessageInputProps extends React.ComponentProps<"div"> {
	chatModule?: "chats" | "cases" | null;
	channelType?: "group" | "direct" | null;
	hideInputTag?: boolean;
	inputMediaOnly?: boolean;
	members?: GroupMember[];
	search?: MessageStoreState["search"];
	onSuccessSubmit?: () => void;
}

const InputText = lazy(() => import("./InputText"));
const MessageInputExternal = lazy(() => import("./InputExternal"));
const MessageInputCases = lazy(() => import("./InputCases"));
const MessageInputChat = lazy(() => import("./InputChat"));
const MessageInputMedia = lazy(() => import("./InputMedia"));
const MessageInputFiles = lazy(() => import("./InputFiles"));
const MessageInputPublish = lazy(() => import("./InputPublish"));
const PublishBox = lazy(() => import("../item/Publish"));
const ReplyBox = lazy(() => import("../item/Replys"));

const MessageInput = (props: MessageInputProps) => {
	const {
		className,
		channelType,
		chatModule,
		members = [],
		search,
		hideInputTag = false,
		inputMediaOnly = false,
		onSuccessSubmit,
		...rest
	} = props;

	const navigate = useNavigate();
	const { create } = useMessageMutation();
	const { watch, getValues, setValue, reset, resetField } =
		useFormContext<PostCreateMessageBodySchema>();

	const replyMessageId = watch("parent_id");
	const replyMessage = watch("message") as MessageDiscussion;
	const preview = watch("preview");
	const tags = watch("tags");
	const messageText = watch("text");
	const isEmptyText = !messageText;
	const isExternal = [
		MESSAGE_TAG.EXTERNAL,
		MESSAGE_TAG.REFERAL,
		MESSAGE_TAG.SPECIALISTS_ADVICE,
		MESSAGE_TAG.HANDOVER,
	].includes(tags as any);

	const isDisabledSwitchTags = useMemo(() => {
		if (!search || !search?.tab) return false;
		return search.tab !== "all";
	}, [search]);

	const hideInputText = useMemo(() => {
		switch (tags) {
			case MESSAGE_TAG.FILES:
			case MESSAGE_TAG.MEDIA:
				return true;
			case MESSAGE_TAG.PUBLISH:
				return isEmpty(preview);
			default:
				return isExternal;
		}
	}, [tags, preview, isExternal]);
	const disableInputText = useMemo(() => {
		switch (tags) {
			case MESSAGE_TAG.PUBLISH:
				return isEmpty(preview);
			default:
				return isEmptyText;
		}
	}, [tags, preview, isEmptyText]);
	const withTags = useMemo(() => {
		switch (tags) {
			case MESSAGE_TAG.DIAGNOSIS:
			case MESSAGE_TAG.PLAN:
			case MESSAGE_TAG.QUESTION:
			case MESSAGE_TAG.TOPIC:
				return true;
			case MESSAGE_TAG.PUBLISH:
				return !isEmpty(preview);
			default:
				return false;
		}
	}, [tags, preview]);

	const onClearPublish = () => {
		resetField("preview");
		resetField("files");
	};

	const onClickSend = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => {
			e.preventDefault();
			const messagePayload = getValues();
			create.mutate(messagePayload);
			reset();
			if (search?.tab && search.tab !== "all") {
				setValue("tags", search.tab);
			}
			setValue("text", "");
			setValue("mentions", []);
			await navigate({
				replace: true,
				// @ts-expect-error
				search: (oldSearch) => ({
					...omit(oldSearch ?? {}, ["tags", "mentions"]),
				}),
			});
			return onSuccessSubmit?.();
		},
		[
			create.mutate,
			navigate,
			getValues,
			reset,
			setValue,
			onSuccessSubmit,
			search?.tab,
		],
	);

	const onClearReply = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		setValue("parent_id", -1);
		setValue("message", null);
		return;
	};

	return (
		<div className={cn("w-full", className)} {...rest}>
			<Suspense>
				<InputText
					hidden={hideInputText}
					tags={tags}
					members={members}
					onSend={onClickSend}
					disabled={disableInputText}
				>
					<Suspense>
						{withTags && tags === "publish" && preview?.title ? (
							<PublishBox
								{...preview}
								size="medium"
								className="py-2"
								onClear={onClearPublish}
							/>
						) : null}
						{chatModule &&
						replyMessage &&
						replyMessageId &&
						replyMessageId === replyMessage.id_db ? (
							<ReplyBox
								chatModule={chatModule}
								message={replyMessage}
								onClear={onClearReply}
							/>
						) : null}
						{chatModule === "chats" && !hideInputTag && channelType ? (
							<MessageInputChat
								channelType={channelType}
								mediaOnly={inputMediaOnly}
								disabled={isDisabledSwitchTags}
							/>
						) : null}
						{chatModule === "cases" && !hideInputTag && !channelType ? (
							<MessageInputCases
								mediaOnly={inputMediaOnly}
								disabled={isDisabledSwitchTags}
							/>
						) : null}
					</Suspense>
				</InputText>
				{tags === "media" ? (
					<MessageInputMedia onSuccessSubmit={onSuccessSubmit} />
				) : null}
				{tags === "files" ? <MessageInputFiles /> : null}
				{!preview?.title && tags === "publish" ? <MessageInputPublish /> : null}
				{isExternal ? <MessageInputExternal isEmptyText={isEmptyText} /> : null}
			</Suspense>
		</div>
	);
};

export default memo(MessageInput, (prevProps, nextProps) => {
	return isEqual(
		omit(prevProps, ["onSuccessSubmit"]),
		omit(nextProps, ["onSuccessSubmit"]),
	);
});
